
import React, { useState, useEffect } from 'react';
import { LabEntry, HospitalLocation } from '../types';
import { dbService } from '../lib/firebase';
import { FlaskConical, Search, Plus, Calendar, Building, CheckCircle2, Circle, Edit2, Trash2, X, Save, User, Filter, Loader2 } from 'lucide-react';

export const LabManager: React.FC = () => {
    const [labs, setLabs] = useState<LabEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDoctorFilter, setSelectedDoctorFilter] = useState('All');
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingLabId, setEditingLabId] = useState<string | null>(null);
    const [form, setForm] = useState<Partial<LabEntry>>({});

    const attendings = ['AESCHLIMANN', 'ALLEN', 'MARQUISS', 'FRIESEN', 'WILSON', 'WEINSTEIN'];
    const residents = ['PYNE', 'PATE', 'BALCOM', 'GRISHAM'];
    const allDoctors = ['All', ...attendings, ...residents];

    // Real-time subscription
    useEffect(() => {
        const unsubscribe = dbService.subscribeToLabs((data) => {
            setLabs(data);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const filteredLabs = labs.filter(lab => {
        const matchesSearch = lab.patientName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                              lab.sampleType.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesDoctor = selectedDoctorFilter === 'All' || 
                              lab.attending === selectedDoctorFilter || 
                              lab.resident === selectedDoctorFilter;

        return matchesSearch && matchesDoctor;
    }).sort((a,b) => new Date(b.submissionDate).getTime() - new Date(a.submissionDate).getTime());

    const handleEdit = (e: React.MouseEvent, lab: LabEntry) => {
        e.stopPropagation();
        e.preventDefault();
        setForm({ ...lab });
        setEditingLabId(lab.id);
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setForm({
            patientName: '',
            sampleType: '',
            hospital: 'Mission',
            submissionDate: new Date().toISOString().split('T')[0],
            submittedTo: '',
            status: 'pending',
            notes: '',
            attending: '',
            resident: ''
        });
        setEditingLabId(null);
        setIsModalOpen(true);
    };

    const handleDelete = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        e.preventDefault();
        await dbService.deleteLab(id);
    };

    const handleSave = async () => {
        if (!form.patientName || !form.sampleType) return;
        
        const newEntry: LabEntry = {
            id: editingLabId || `lab-${Date.now()}`,
            patientName: form.patientName,
            sampleType: form.sampleType,
            hospital: form.hospital as HospitalLocation,
            submissionDate: form.submissionDate || '',
            submittedTo: form.submittedTo || '',
            status: form.status as 'pending' | 'received',
            notes: form.notes || '',
            attending: form.attending || '',
            resident: form.resident || ''
        };

        setIsModalOpen(false);
        await dbService.saveLab(newEntry);
    };

    const toggleStatus = async (lab: LabEntry) => {
        const newStatus: 'pending' | 'received' = lab.status === 'pending' ? 'received' : 'pending';
        const updatedLab: LabEntry = { ...lab, status: newStatus };
        await dbService.saveLab(updatedLab);
    };

    return (
        <div className="h-full flex flex-col p-6 overflow-hidden relative">
             <div className="mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-3 brand-font">
                        <FlaskConical className="w-8 h-8 text-blue-900 dark:text-blue-400" />
                        Lab Tracking
                    </h2>
                    <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">Manage pending samples and results.</p>
                </div>
                
                <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
                    {/* Doctor Filter */}
                    <div className="relative md:w-48">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Filter className="h-4 w-4 text-slate-400" />
                        </div>
                        <select 
                            value={selectedDoctorFilter}
                            onChange={(e) => setSelectedDoctorFilter(e.target.value)}
                            className="block w-full pl-10 pr-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-blue-900 focus:outline-none appearance-none font-medium text-slate-900 dark:text-white"
                        >
                            {allDoctors.map(doc => (
                                <option key={doc} value={doc}>{doc === 'All' ? 'Filter by Doctor' : doc}</option>
                            ))}
                        </select>
                    </div>

                    <div className="relative flex-1 md:w-64">
                        <input
                            type="text"
                            placeholder="Search patients or tests..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-900 transition-all bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400"
                        />
                        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                    </div>
                    <button 
                        onClick={handleAddNew}
                        className="bg-blue-900 hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-colors shadow-sm whitespace-nowrap"
                    >
                        <Plus className="w-5 h-5" /> Add Lab
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="flex justify-center items-center h-40">
                        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4">
                        {filteredLabs.length === 0 ? (
                            <div className="text-center py-20 text-slate-400 font-medium">No labs found matching your criteria.</div>
                        ) : (
                            filteredLabs.map(lab => (
                                <div key={lab.id} className={`bg-white dark:bg-slate-800 rounded-xl p-5 border shadow-md transition-all hover:shadow-lg flex flex-col md:flex-row gap-4 items-start md:items-center ${lab.status === 'received' ? 'border-l-4 border-l-green-600 border-slate-200 dark:border-slate-700' : 'border-l-4 border-l-amber-400 border-slate-200 dark:border-slate-700'}`}>
                                    <div className="flex-1 space-y-1">
                                        <div className="flex items-center gap-3">
                                            <h3 className="text-lg font-bold text-slate-900 dark:text-white font-serif">{lab.patientName}</h3>
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide border ${lab.hospital === 'Mission' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-300 border-blue-200 dark:border-blue-800' : 'bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300 border-teal-200 dark:border-teal-800'}`}>
                                                {lab.hospital}
                                            </span>
                                        </div>
                                        <p className="font-medium text-slate-700 dark:text-slate-300">{lab.sampleType}</p>
                                        
                                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500 dark:text-slate-400 mt-2">
                                            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Submitted: {lab.submissionDate}</span>
                                            <span className="flex items-center gap-1.5"><Building className="w-4 h-4" /> To: {lab.submittedTo}</span>
                                        </div>

                                        {(lab.attending || lab.resident) && (
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {lab.attending && (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-100 dark:border-red-900/50 uppercase">
                                                        <User className="w-3 h-3" /> Attending: {lab.attending}
                                                    </span>
                                                )}
                                                {lab.resident && (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-sky-50 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300 border border-sky-100 dark:border-sky-900/50 uppercase">
                                                        <User className="w-3 h-3" /> Resident: {lab.resident}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {lab.notes && <p className="text-sm text-slate-500 dark:text-slate-400 italic mt-1 bg-slate-50 dark:bg-slate-900/50 p-2 rounded inline-block">{lab.notes}</p>}
                                    </div>

                                    <div className="flex items-center gap-3 w-full md:w-auto pt-4 md:pt-0 border-t md:border-t-0 border-slate-100 dark:border-slate-700">
                                        <button 
                                            onClick={() => toggleStatus(lab)}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border transition-colors ${lab.status === 'received' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/50' : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/50'}`}
                                        >
                                            {lab.status === 'received' ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                                            {lab.status === 'received' ? 'RECEIVED' : 'PENDING'}
                                        </button>
                                        <div className="flex gap-1">
                                            <button onClick={(e) => handleEdit(e, lab)} className="p-2 text-slate-400 hover:text-blue-900 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button onClick={(e) => handleDelete(e, lab.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-lg w-full overflow-hidden flex flex-col max-h-[90vh] border border-slate-200 dark:border-slate-700">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex justify-between items-center">
                            <h3 className="font-bold text-slate-800 dark:text-white brand-font">{editingLabId ? 'Edit Lab Entry' : 'Add New Lab'}</h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6 space-y-4 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Patient Name</label>
                                    <input className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                        value={form.patientName || ''} onChange={e => setForm({...form, patientName: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Sample Type</label>
                                    <input className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" placeholder="e.g. Biopsy, Cytology"
                                        value={form.sampleType || ''} onChange={e => setForm({...form, sampleType: e.target.value})} />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Hospital Location</label>
                                    <div className="flex gap-2">
                                        <button 
                                            onClick={() => setForm({...form, hospital: 'Mission'})}
                                            className={`flex-1 py-2 text-sm font-bold rounded border ${form.hospital === 'Mission' ? 'bg-blue-900 dark:bg-blue-600 text-white border-blue-900 dark:border-blue-600' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600'}`}
                                        >Mission</button>
                                        <button 
                                            onClick={() => setForm({...form, hospital: 'OP'})}
                                            className={`flex-1 py-2 text-sm font-bold rounded border ${form.hospital === 'OP' ? 'bg-blue-800 dark:bg-blue-700 text-white border-blue-800 dark:border-blue-700' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600'}`}
                                        >OP</button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Submission Date</label>
                                    <input type="date" className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                        value={form.submissionDate || ''} onChange={e => setForm({...form, submissionDate: e.target.value})} />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-red-700 dark:text-red-400 uppercase mb-1">Attending</label>
                                    <select 
                                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 uppercase text-slate-900 dark:text-white"
                                        value={form.attending || ''} 
                                        onChange={(e) => setForm({...form, attending: e.target.value})}
                                    >
                                        <option value="">-- Select --</option>
                                        {attendings.map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-blue-700 dark:text-blue-400 uppercase mb-1">Resident</label>
                                    <select 
                                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 uppercase text-slate-900 dark:text-white"
                                        value={form.resident || ''} 
                                        onChange={(e) => setForm({...form, resident: e.target.value})}
                                    >
                                        <option value="">-- Select --</option>
                                        {residents.map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Submitted To</label>
                                <input className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" placeholder="e.g. Antech, Idexx, University"
                                    value={form.submittedTo || ''} onChange={e => setForm({...form, submittedTo: e.target.value})} />
                            </div>

                             <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Status</label>
                                <select 
                                    className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                    value={form.status} onChange={(e) => setForm({...form, status: e.target.value as any})}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="received">Received</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Notes</label>
                                <textarea className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none h-20 resize-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                    value={form.notes || ''} onChange={e => setForm({...form, notes: e.target.value})} />
                            </div>
                        </div>
                        <div className="p-4 bg-slate-50 dark:bg-slate-900 flex justify-end gap-2 border-t border-slate-200 dark:border-slate-700">
                            <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-600 dark:text-slate-400 font-medium hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
                            <button onClick={handleSave} className="px-4 py-2 bg-blue-900 dark:bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-800 dark:hover:bg-blue-500 flex items-center gap-2">
                                <Save className="w-4 h-4" /> Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
